/**
 * Rapporto di una discovery: esito, numeri e registro degli eventi.
 *
 * Risponde alla domanda che lo stato del job da solo non copre: *cosa* è stato
 * interrogato e *cosa* è andato storto. Un job "riuscito" che ha ricevuto 403
 * su tre servizi su sei è un esito diverso da un job riuscito, e qui si vede.
 */
import { t } from '../i18n/lingua'
import { useState } from 'react'

import { api } from '../api/client'
import type { Job, JobEvent } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { EmptyState, ErrorBanner, Spinner, formatDateTime } from './common'
import { DataTable, type Column } from './DataTable'
import { useAsync } from '../hooks/useAsync'
import {
  collected,
  describeTypes,
  duration,
  ingested,
  planSteps,
  type StepRow,
} from '../jobReport'

// Le etichette passano da funzioni con `t('…')` letterale (non da una mappa di
// stringhe fisse): così la guardia delle traduzioni le vede, e a runtime seguono
// la lingua scelta. Una mappa `const` con valori italiani sfuggiva a entrambe.
function levelLabel(level: string): string {
  switch (level) {
    case 'debug':
      return t('Dettaglio')
    case 'info':
      return t('Info')
    case 'warning':
      return t('Avviso')
    case 'error':
      return t('Errore')
    default:
      return level
  }
}

/** Il protocollo usa "warning", i badge della console "warn". */
const BADGE_CLASS: Record<string, string> = {
  debug: 'muted',
  info: 'info',
  warning: 'warn',
  error: 'error',
}

/** Etichette leggibili per le statistiche, che nel DB hanno nomi tecnici. */
function statLabel(key: string): string {
  const m: Record<string, string> = {
    ci_ricevuti: t('CI ricevuti'),
    ci_accettati: t('CI salvati'),
    ci_senza_chiave: t('Scartati senza chiave'),
    ci_oltre_quota: t('Rifiutati per quota'),
    relazioni_ricevute: t('Relazioni ricevute'),
    relazioni_accettate: t('Relazioni risolte'),
    batch: t('Batch ricevuti'),
  }
  return m[key] ?? key
}

/**
 * Etichette leggibili per le chiavi ricorrenti dei dettagli.
 *
 * Le chiavi arrivano dalla sonda in forma compatta perché sono anche filtrabili;
 * mostrarle così com'è renderebbe il rapporto leggibile solo a chi lo ha scritto.
 */
function detailLabel(key: string): string {
  const m: Record<string, string> = {
    indirizzo: t('IP'),
    passo: t('passo'),
    tipo: t('tipo'),
    bersagli: t('indirizzi nell’ambito'),
    eseguito_su: t('eseguito su'),
    riusciti: t('riusciti'),
    falliti: t('falliti'),
    saltati_condizione: t('saltati per condizione'),
    saltati_sconosciuto: t('passo sconosciuto alla sonda'),
    attributi_raccolti: t('attributi raccolti'),
    interfacce_raccolte: t('interfacce raccolte'),
    relazioni_raccolte: t('adiacenze raccolte'),
    bersagli_arricchiti: t('indirizzi arricchiti'),
    ci_raccolti: t('oggetti raccolti'),
    ci_nuovi: t('nuovi'),
    ci_aggiornati: t('già noti'),
    per_classe: t('per classe'),
    prodotti_sw: t('prodotti software'),
    schede_macchina: t('schede macchina'),
    relazioni: t('adiacenze'),
    vivo_icmp: t('risponde al ping'),
    esito: t('esito'),
    spiegazione: t('causa probabile'),
    proprieta: t('proprietà'),
    proprieta_per_ci: t('proprietà per risorsa'),
    interfacce: t('interfacce'),
    indirizzi_ip: t('indirizzi IP'),
    vicinati: t('vicinati'),
    identificativi: t('identificativi'),
    non_leggibili: t('non leggibili'),
    credenziale: t('credenziale'),
    credenziali_provate: t('credenziali provate'),
    tipo_cred: t('tipo'),
    durata_ms: t('durata'),
    approfondita: t('approfondita'),
    per_tipo: t('per tipo'),
    senza_nome: t('senza nome'),
    numero: t('numero'),
    omessi: t('omessi'),
    parallelismo: t('interrogazioni parallele'),
    core_sonda: t('core della sonda'),
    origine: t('deciso da'),
    rate_richiesto: t('pacchetti/s richiesti'),
    rate_effettivo: t('pacchetti/s effettivi'),
    finestra_probe: t('attesa per interrogazione'),
    tetto_macchina: t('massimo per questa sonda'),
    serve_per_rate: t('servirebbero'),
  }
  return m[key] ?? key
}

/** Da dove viene il parallelismo scelto, in parole. */
function sourceLabel(value: string): string {
  const m: Record<string, string> = {
    auto: t('dimensionamento automatico'),
    spec: t('impostato nel job'),
    config: t('tetto della sonda'),
  }
  return m[value] ?? value
}

function formatDetail(key: string, value: unknown): string {
  if (key === 'origine' && typeof value === 'string') {
    return sourceLabel(value)
  }
  if (key === 'durata_ms' && typeof value === 'number') {
    return value >= 1000 ? `${(value / 1000).toFixed(1)} s` : `${value} ms`
  }
  if (typeof value === 'boolean') return value ? t('sì') : t('no')
  // Un elenco (gli esempi, i nomi delle macchine) è un elenco: «VM-A, VM-B».
  // Trattato come oggetto usciva «0 ×VM-A, 1 ×VM-B», con gli indici al posto
  // dei conteggi; e un elenco vuoto (o `null`) è «nessuno», non «null».
  if (Array.isArray(value)) {
    return value.length === 0
      ? t('nessuno')
      : value.map((v) => (v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ')
  }
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') {
    // Le ripartizioni (`per_tipo`, `per_fase`) sono oggetti: appiattirle in
    // "chiave ×n" si legge, il JSON grezzo no.
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k} ×${v}`)
      .join(', ')
  }
  return String(value)
}

/** The event's message with its details as chips and the session sample. */
function EventMessage({ event }: { event: JobEvent }) {
  const tutti = event.details ?? {}
  // Il «campione» — ciò che l'apparato ha mandato in una sessione fallita — non è
  // un chip: è un blocco di testo che si legge e si copia per l'analisi. Sta a
  // parte, in un riquadro apribile.
  const campione = typeof tutti['campione'] === 'string' ? (tutti['campione'] as string) : ''
  // I controlli della diagnosi hanno il loro riquadro in cima al rapporto.
  const details = dettagliEvento(event)
  return (
    <>
      {/* Il messaggio passa da `t` come tutto il resto, e i suoi `details`
          sono i valori dei segnaposto.
          
          Il messaggio **è** il modello: «salvate {salvate} su {totale}» con i
          numeri in `details`. Non serve un codice separato — sarebbe un
          secondo vocabolario da tenere allineato al primo, la stessa cosa
          che si è evitata in console.
          
          E le righe scritte prima di questo cambiamento continuano a
          funzionare: il loro messaggio è già interpolato, non ha segnaposto,
          e `t` restituisce il testo così com'è. Una storia scritta in
          italiano resta leggibile, semplicemente non si traduce — che è
          corretto, perché è quello che è successo davvero. */}
      {t(event.message, event.details ?? {})}
      {details.length > 0 && (
        <div className="event__details">
          {details.map(([key, value]) => (
            <span key={key} className="chip">
              {detailLabel(key)}: {formatDetail(key, value)}
            </span>
          ))}
        </div>
      )}
      {campione && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ cursor: 'pointer' }}>
            {t('Campione della sessione (per l’analisi)')}
          </summary>
          <pre
            className="mono"
            style={{
              maxHeight: 320,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              marginTop: 6,
              padding: 8,
              background: 'var(--surface-2, rgba(0,0,0,0.04))',
              borderRadius: 6,
            }}
          >
            {campione}
          </pre>
        </details>
      )}
    </>
  )
}

/** The details shown as chips (the sample and the diagnosis checks have their own place). */
function dettagliEvento(event: JobEvent): Array<[string, unknown]> {
  return Object.entries(event.details ?? {}).filter(([k]) => k !== 'campione' && k !== 'controlli')
}

/** The event log's columns. */
function colonneEventi(): Column<JobEvent>[] {
  return [
    {
      key: 'level', header: t('Livello'), value: (e) => levelLabel(e.level),
      render: (e) => (
        <span className={`badge badge--${BADGE_CLASS[e.level] ?? 'muted'}`}>
          {levelLabel(e.level)}
        </span>
      ),
    },
    { key: 'stage', header: t('Fase'), mono: true },
    {
      key: 'message', header: t('Evento'),
      value: (e) => [
        t(e.message, e.details ?? {}),
        ...dettagliEvento(e).map(([key, value]) => `${detailLabel(key)}: ${formatDetail(key, value)}`),
      ].join(' '),
      render: (e) => <EventMessage event={e} />,
    },
    {
      key: 'when', header: t('Quando'), type: 'date', mono: true,
      value: (e) => e.occurred_at ?? e.created_at,
      render: (e) => formatDateTime(e.occurred_at ?? e.created_at),
    },
  ]
}

/**
 * I problemi di una scansione, raggruppati per causa.
 *
 * Il registro cronologico risponde a «cosa è successo alle 02:14» e non a
 * «cosa non è andato su questa /24»: la stessa causa — un timeout SSH, una
 * credenziale rifiutata — vi compare duecento volte, una per host, mischiata
 * alle altre. Qui si raggruppa per causa (il messaggio senza il bersaglio),
 * si ordina per quanti host colpisce, e gli host stanno sotto come gettoni:
 * la domanda a cui risponde è «qual È il problema, e su chi».
 */
function ProblemiRaggruppati({ events }: { events: JobEvent[] }) {
  const problemi = new Map<
    string,
    { level: string; stage: string; messaggio: string; bersagli: string[] }
  >()
  for (const evento of events) {
    if (evento.level !== 'error' && evento.level !== 'warning') continue
    const bersaglio = String(evento.details?.bersaglio ?? '')
    // La causa è il messaggio SENZA il bersaglio: «10.0.10.5: passo «ssh» non
    // riuscito: timeout» e la stessa riga su .6 sono lo stesso problema.
    let causa = evento.message
    if (bersaglio && causa.startsWith(`${bersaglio}: `)) {
      causa = causa.slice(bersaglio.length + 2)
    }
    const chiave = `${evento.level}|${evento.stage}|${causa}`
    const voce = problemi.get(chiave) ?? {
      level: evento.level,
      stage: evento.stage,
      messaggio: causa,
      bersagli: [],
    }
    if (bersaglio && !voce.bersagli.includes(bersaglio)) voce.bersagli.push(bersaglio)
    if (!bersaglio) voce.bersagli.push('')
    problemi.set(chiave, voce)
  }

  const ordinati = [...problemi.values()].sort((a, b) => {
    if (a.level !== b.level) return a.level === 'error' ? -1 : 1
    return b.bersagli.length - a.bersagli.length
  })

  if (ordinati.length === 0) {
    return (
      <EmptyState
        title={t('Nessun problema')}
        hint={t('Nessun errore o avviso in questa scansione.')}
      />
    )
  }

  const MOSTRATI = 16
  return (
    <div className="problemi">
      {ordinati.map((problema) => (
        <div key={problema.level + problema.stage + problema.messaggio} className="problemi__voce">
          <div className="problemi__testa">
            <span className={`badge badge--${BADGE_CLASS[problema.level] ?? 'muted'}`}>
              {levelLabel(problema.level)}
            </span>
            <span className="mono text-muted">{problema.stage || '—'}</span>
            <strong>
              {problema.bersagli.filter(Boolean).length || problema.bersagli.length}{' '}
              {(problema.bersagli.filter(Boolean).length || problema.bersagli.length) === 1
                ? t('host')
                : t('host')}
            </strong>
          </div>
          <p className="problemi__messaggio">{t(problema.messaggio)}</p>
          {problema.bersagli.filter(Boolean).length > 0 && (
            <div className="chips-dato">
              {problema.bersagli
                .filter(Boolean)
                .slice(0, MOSTRATI)
                .map((b) => (
                  <span key={b}>{b}</span>
                ))}
              {problema.bersagli.filter(Boolean).length > MOSTRATI && (
                <details className="problemi__altri">
                  <summary>+{problema.bersagli.filter(Boolean).length - MOSTRATI}</summary>
                  <div className="chips-dato" style={{ marginTop: 4 }}>
                    {problema.bersagli
                      .filter(Boolean)
                      .slice(MOSTRATI)
                      .map((b) => (
                        <span key={b}>{b}</span>
                      ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}


/**
 * Il riquadro di scarico della cattura MIB, per i soli job di campionamento.
 *
 * La cattura è il materiale con cui si scrive un driver: si scarica come file
 * JSON e si passa allo strumento `censo-snmp-driver`. Esiste solo dopo che il
 * job ha campionato l'apparato; finché non c'è, il pulsante lo dice invece di
 * dare un file vuoto.
 */
function CaptureDownload({ job }: { job: Job }) {
  const [errore, setErrore] = useState<string | null>(null)
  const [scaricando, setScaricando] = useState(false)

  const scarica = async () => {
    setErrore(null)
    setScaricando(true)
    try {
      const cattura = await api.jobCapture(job.id)
      const blob = new Blob([JSON.stringify(cattura, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `campione-${(cattura.sys_object_id || job.id).replace(/[^\w.-]+/g, '_')}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      setErrore(t('Cattura non ancora disponibile: il job deve completare il campionamento.'))
    } finally {
      setScaricando(false)
    }
  }

  return (
    <div className="banner banner--info">
      <div>
        <strong>{t('Campionamento SNMP')}.</strong>{' '}
        {t('La cattura dell’albero MIB serve a generare un driver di arricchimento.')}
      </div>
      <div style={{ marginTop: 8 }}>
        <button type="button" className="secondary" onClick={scarica} disabled={scaricando}>
          {scaricando ? t('Scarico…') : t('Scarica cattura (JSON)')}
        </button>
      </div>
      {errore && (
        <div className="text-muted" style={{ marginTop: 6 }}>
          {errore}
        </div>
      )}
      <DriverDraft job={job} />
    </div>
  )
}

function draftStatusLabel(s: string): string {
  if (s === 'approved') return t('approvato: in uso su tutte le sonde')
  if (s === 'disabled') return t('disattivato')
  return t('in attesa di approvazione dall’amministrazione della piattaforma')
}

/**
 * The driver drafted from this sample with the tenant's vendor MIBs
 * (Network → «MIB di prodotto»): generate or regenerate it, read what it
 * names and what it leaves out. The platform administration approves it.
 */
function DriverDraft({ job }: { job: Job }) {
  const { principal } = useAuth()
  const ruoli = principal?.roles ?? []
  const admin = ruoli.includes('tenant_admin') || ruoli.includes('provider_admin')
  const draft = useAsync(() => api.jobDriverDraft(job.id), [job.id])
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  async function genera() {
    setErrore(null)
    setLavoro(true)
    try {
      await api.createJobDriverDraft(job.id)
      draft.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  const d = draft.data
  return (
    <div style={{ marginTop: 12 }}>
      <div>
        {t('Con i MIB del costruttore caricati nella pagina Rete, CENSO genera dalla cattura una bozza di driver con i nomi veri: tabelle, stati decodificati, numero di serie e versione, CPU e memoria per Eagle. La approva l’amministrazione della piattaforma, poi vale su tutte le sonde.')}
      </div>
      {admin && d?.status !== 'approved' && (
        <div style={{ marginTop: 8 }}>
          <button type="button" onClick={() => void genera()} disabled={lavoro}>
            {lavoro ? t('Genero…') : d ? t('Rigenera la bozza di driver') : t('Genera bozza di driver')}
          </button>
        </div>
      )}
      <ErrorBanner error={errore} />
      {d && (
        <div style={{ marginTop: 8 }}>
          <strong>{d.label}</strong> · <span className="mono">{d.driver_id}</span>
          <div className="text-muted">{draftStatusLabel(d.status)}</div>
          <pre className="mono" style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>{d.report}</pre>
        </div>
      )}
    </div>
  )
}

/**
 * Titolo e descrizione del job, in testa al rapporto, con la modifica in
 * linea: un job già eseguito con il titolo sbagliato resta nell'elenco per un
 * mese, e rilanciarlo per correggere una parola non ha senso.
 */
function TitoloJob({ job, onSaved }: { job: Job; onSaved?: (aggiornato: Job) => void }) {
  const [modifica, setModifica] = useState(false)
  const [titolo, setTitolo] = useState(job.title ?? '')
  const [descrizione, setDescrizione] = useState(job.description ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function salva() {
    setBusy(true)
    setError(null)
    try {
      const aggiornato = await api.updateJob(job.id, {
        title: titolo.trim() || null,
        description: descrizione.trim() || null,
      })
      setModifica(false)
      onSaved?.(aggiornato)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  if (modifica) {
    return (
      <form
        className="form-grid"
        style={{ marginBottom: 12 }}
        onSubmit={(e) => {
          e.preventDefault()
          void salva()
        }}
      >
        <ErrorBanner error={error} />
        <div className="field">
          <label htmlFor={`job-title-${job.id}`}>{t('Titolo')}</label>
          <input id={`job-title-${job.id}`} value={titolo} onChange={(e) => setTitolo(e.target.value)} maxLength={160} autoFocus />
        </div>
        <div className="field">
          <label htmlFor={`job-description-${job.id}`}>{t('Descrizione')}</label>
          <input id={`job-description-${job.id}`} value={descrizione} onChange={(e) => setDescrizione(e.target.value)} maxLength={4000} />
        </div>
        <div className="rowactions" style={{ textAlign: 'left' }}>
          <button type="submit" disabled={busy}>{busy ? t('Salvataggio…') : t('Salva')}</button>
          <button type="button" className="secondary" onClick={() => setModifica(false)} disabled={busy}>
            {t('Annulla')}
          </button>
        </div>
      </form>
    )
  }
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 8 }}>
      <strong>{job.title ?? <span className="text-muted">{t('Senza titolo')}</span>}</strong>
      {job.description && <span className="text-muted">{job.description}</span>}
      <button type="button" className="secondary" onClick={() => setModifica(true)}>
        {t('Modifica titolo')}
      </button>
    </div>
  )
}

export default function JobReport({ job, onMeta }: { job: Job; onMeta?: (aggiornato: Job) => void }) {
  const [level, setLevel] = useState('')
  const [stage, setStage] = useState('')
  // La vista parte da «per problema» quando i guai sono tanti: è la domanda
  // che si sta facendo chi apre il rapporto di una /24 andata male.
  const guai = (job.event_counts?.error ?? 0) + (job.event_counts?.warning ?? 0)
  const [vista, setVista] = useState<'registro' | 'problemi'>(guai > 15 ? 'problemi' : 'registro')
  const report = useAsync(
    () => api.jobReport(job.id, { level: level || undefined, stage: stage || undefined }),
    [job.id, level, stage],
  )
  // Le fasi si scoprono dal rapporto stesso: variano per connettore (i servizi
  // cloud, le fasi di una scansione SNMP) e un elenco fisso andrebbe fuori
  // sincrono a ogni connettore aggiunto. Il rapporto non filtrato è la fonte,
  // così restringere per livello non fa sparire le voci dell'elenco.
  const allStages = useAsync(() => api.jobReport(job.id), [job.id])
  const stages = Array.from(
    new Set((allStages.data?.events ?? []).map((e) => e.stage).filter(Boolean)),
  ).sort()

  const counts = job.event_counts ?? {}
  const stats = Object.entries(job.stats ?? {}).filter(([, v]) => v !== 0)

  // Il divario tra raccolto e salvato è il numero che spiega un inventario più
  // piccolo del previsto: va mostrato esplicitamente, non lasciato da calcolare.
  const persi = (job.stats?.ci_ricevuti ?? 0) - (job.stats?.ci_accettati ?? 0)

  return (
    <div className="report">
      <TitoloJob job={job} onSaved={onMeta} />
      {/* Da dove è partita la chiamata: per un connettore cloud è ciò che dice
          quale IP il provider ha visto e quali regole di rete ha attraversato —
          la sonda in sede o il Control Plane. */}
      {allStages.data?.executed_by && (
        <p className="text-muted" style={{ marginTop: 0 }}>
          {t('Eseguito da')}: {allStages.data.executed_by}
        </p>
      )}

      {job.error_message && (
        <div className="banner banner--error">
          <strong>{t('Il job è terminato con errore:')}</strong> {job.error_message}
        </div>
      )}

      {job.kind === 'snmp_sample' && <CaptureDownload job={job} />}

      {persi > 0 && (
        <div className="banner banner--warn">
          {t(
            '{persi} CI raccolti dalla sonda non sono finiti in CMDB. Il motivo è negli eventi di fase «ingest» qui sotto.',
            { persi },
          )}
        </div>
      )}

      {/* La diagnosi dei connettori cloud: prima di tutto il resto, perché
          risponde alla domanda di chi configura un account nuovo — «cosa
          manca, e cosa devo fare». */}
      <Diagnosi events={allStages.data?.events ?? []} />

      {/* Cosa è successo, prima del registro di cosa è andato storto. Un
          registro risponde bene alla seconda domanda e male alla prima. */}
      <PlanOutcome events={allStages.data?.events ?? []} />

      {stats.length > 0 && (
        <div className="report__stats">
          {stats.map(([key, value]) => (
            <div key={key} className="report__stat">
              <span className="report__stat-value">{value}</span>
              <span className="report__stat-label">{statLabel(key)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="report__filters">
        <button
          type="button"
          className={vista === 'registro' ? 'chip chip--active' : 'chip'}
          onClick={() => setVista('registro')}
        >
          {t('Registro')}
        </button>
        <button
          type="button"
          className={vista === 'problemi' ? 'chip chip--active' : 'chip'}
          onClick={() => setVista('problemi')}
          disabled={guai === 0}
        >
          {t('Per problema')} {guai ? `(${guai})` : ''}
        </button>
        <span style={{ width: 12 }} />
        <button
          type="button"
          className={level === '' ? 'chip chip--active' : 'chip'}
          onClick={() => setLevel('')}
          style={vista === 'problemi' ? { display: 'none' } : undefined}
        >
          {t('Tutti')}
        </button>
        {(['error', 'warning', 'info', 'debug'] as const).map((l) => (
          <button
            key={l}
            type="button"
            className={level === l ? 'chip chip--active' : 'chip'}
            style={vista === 'problemi' ? { display: 'none' } : undefined}
            onClick={() => setLevel(l)}
            // Un livello senza eventi non va offerto: cliccarlo darebbe una
            // lista vuota indistinguibile da un rapporto assente.
            disabled={!counts[l]}
          >
            {levelLabel(l)} {counts[l] ? `(${counts[l]})` : ''}
          </button>
        ))}

        {stages.length > 1 && vista !== 'problemi' && (
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            style={{ marginLeft: 8, width: 'auto' }}
            aria-label={t('Filtra per fase')}
          >
            <option value="">{t('Tutte le fasi')}</option>
            {stages.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        )}
      </div>

      <ErrorBanner error={report.error} />

      {vista === 'problemi' ? (
        <ProblemiRaggruppati events={allStages.data?.events ?? []} />
      ) : report.loading && !report.data ? (
        <Spinner />
      ) : !report.data?.events.length ? (
        <EmptyState
          title={t('Nessun evento')}
          hint={
            job.status === 'pending' || job.status === 'assigned'
              ? t('Il job non è ancora stato eseguito da una sonda.')
              : // Il rapporto viaggia con i risultati: se il Control Plane li ha
                // rifiutati, è andato perso con loro. Dire «aggiorna la sonda»
                // mandava a cercare il guasto dalla parte sbagliata.
                (job.error_message ?? '').includes('/appliance/results')
                ? t('Il Control Plane ha rifiutato i risultati della sonda (vedi l’errore sopra), e il rapporto viaggiava con loro. Non è la sonda: ritenta il job quando il problema del Control Plane è risolto.')
                : t('La sonda che ha eseguito questo job non produce ancora un rapporto: aggiornala.')
          }
        />
      ) : (
        <>
          <DataTable
            rows={report.data.events}
            rowKey={(e) => e.id}
            conteggio={{ nome: t('eventi') }}
            columns={colonneEventi()}
          />
          {report.data.truncated && (
            <p className="field__hint">
              {t('Mostrati {n} eventi su {tot}: usa i filtri per restringere.', {
                n: report.data.events.length,
                tot: report.data.total_events,
              })}
            </p>
          )}
        </>
      )}
    </div>
  )
}


/** L'esito di ciascun passo, e cosa la scansione ha raccolto.
 *
 *  Una funzione e non una costante di modulo: `t` va chiamata quando l'esito
 *  si mostra, non quando il file si importa. */
function esitoPasso(outcome: StepRow['outcome']): { label: string; badge: string; title: string } {
  switch (outcome) {
    case 'ok':
      return { label: 'OK', badge: 'ok', title: t('Eseguito, con dati raccolti.') }
    case 'vuoto':
      return {
        label: t('A vuoto'),
        badge: 'warn',
        title: t('Eseguito e non ha raccolto niente: gli host hanno risposto e non avevano quel dato, oppure il passo cerca qualcosa che lì non c’è.'),
      }
    case 'mai':
      return {
        label: t('Mai partito'),
        badge: 'warn',
        title: t('Non è partito su nessun indirizzo: la condizione non è mai stata soddisfatta. La scansione risulta riuscita e i dati di questo passo non ci sono.'),
      }
    case 'sconosciuto':
      return {
        label: t('Sconosciuto alla sonda'),
        badge: 'error',
        title: t('Questa sonda non sa eseguire il passo: va aggiornata.'),
      }
  }
}

/** What a step collected, in words («12 attributi su 4 indirizzi, 3 interfacce»). */
function raccoltoPasso(step: StepRow): string {
  return [
    step.attributes > 0 &&
      t('{n} attributi su {m} indirizzi', { n: step.attributes, m: step.enriched }),
    step.interfaces > 0 && t('{n} interfacce', { n: step.interfaces }),
    step.relations > 0 && t('{n} adiacenze', { n: step.relations }),
  ]
    .filter(Boolean)
    .join(', ')
}

function PlanOutcome({ events }: { events: JobEvent[] }) {
  const steps = planSteps(events)
  const raccolto = collected(events)
  const inCmdb = ingested(events)

  if (steps.length === 0 && !raccolto) return null

  return (
    <div className="report__plan">
      {raccolto && (
        <p className="report__collected">
          {t('{n} oggetti raccolti ({tipi})', { n: raccolto.cis, tipi: describeTypes(raccolto.byType) })}
          {raccolto.interfaces > 0 && <>, {t('{n} interfacce', { n: raccolto.interfaces })}</>}
          {raccolto.relations > 0 && <>, {t('{n} adiacenze', { n: raccolto.relations })}</>}
          {raccolto.software > 0 && <>, {t('{n} prodotti software', { n: raccolto.software })}</>}
          {raccolto.inventories > 0 && <>, {t('{n} schede macchina', { n: raccolto.inventories })}</>}
          {inCmdb && (
            <>
              {' · '}
              {t('in CMDB: {nuovi} nuovi, {noti} già noti', { nuovi: inCmdb.created, noti: inCmdb.updated })}
              {inCmdb.received > inCmdb.accepted && (
                <> — {t('{n} non salvati, vedi la fase «ingest»', { n: inCmdb.received - inCmdb.accepted })}</>
              )}
            </>
          )}
        </p>
      )}

      {steps.length > 0 && (
        <DataTable
          rows={steps}
          rowKey={(step) => step.id}
          conteggio={{ nome: t('passi') }}
          columns={[
            {
              key: 'id', header: t('Passo'), value: (step) => `${step.id} ${step.probe}`,
              render: (step) => (
                <>
                  <strong>{step.id}</strong>{' '}
                  <span className="text-muted mono">{step.probe}</span>
                </>
              ),
            },
            {
              key: 'outcome', header: t('Esito'), value: (step) => esitoPasso(step.outcome).label,
              render: (step) => (
                <span
                  className={`badge badge--${esitoPasso(step.outcome).badge}`}
                  title={esitoPasso(step.outcome).title}
                >
                  {esitoPasso(step.outcome).label}
                </span>
              ),
            },
            {
              key: 'executed', header: t('Eseguito su'), type: 'number', value: (step) => step.executed,
              render: (step) => (
                <>
                  {step.executed}
                  {step.targets > 0 && (
                    <span className="text-muted"> / {step.targets}</span>
                  )}
                </>
              ),
            },
            { key: 'ok', header: t('Riusciti'), type: 'number', render: (step) => step.ok },
            { key: 'failed', header: t('Falliti'), type: 'number', render: (step) => step.failed || '—' },
            {
              key: 'skipped', header: t('Saltati'), type: 'number',
              value: (step) => step.skippedCondition + step.skippedUnknown,
              render: (step) => (
                // I due motivi restano separati: «la sonda non conosce il
                // passo» si risolve aggiornandola, «la condizione non era
                // soddisfatta» riscrivendo il piano — o non si risolve
                // affatto, perché è ciò che si voleva.
                step.skippedCondition || step.skippedUnknown ? (
                  <>
                    {step.skippedCondition > 0 && (
                      <span title={t('La condizione non era soddisfatta su questi indirizzi.')}>
                        {t('{n} per condizione', { n: step.skippedCondition })}
                      </span>
                    )}
                    {step.skippedUnknown > 0 && (
                      <span title={t('Questa sonda non sa eseguire il passo.')}>
                        {' '}
                        {t('{n} passo ignoto', { n: step.skippedUnknown })}
                      </span>
                    )}
                  </>
                ) : (
                  '—'
                )
              ),
            },
            {
              key: 'collected', header: t('Raccolto'), value: (step) => raccoltoPasso(step),
              render: (step) => <span className="text-muted">{raccoltoPasso(step) || '—'}</span>,
            },
            {
              key: 'duration', header: t('Durata'), type: 'number', mono: true,
              value: (step) => step.durationMs, render: (step) => duration(step.durationMs),
            },
          ]}
        />
      )}
    </div>
  )
}

type ControlloDiagnosi = {
  area: string
  controllo: string
  esito: 'ok' | 'problema' | 'avviso' | 'spento'
  dettaglio?: string
  rimedio?: string
}

const ICONA_ESITO: Record<ControlloDiagnosi['esito'], string> = {
  ok: '✓',
  problema: '✗',
  avviso: '!',
  spento: '–',
}

/**
 * La diagnosi di un connettore cloud (evento di fase «diagnosi» scritto dalla
 * sonda, vedi appliance/internal/discovery/cloud/aws_diagnosi.go): ogni
 * controllo con esito, cosa è tornato e cosa fare. I problemi prima, poi gli
 * avvisi; quelli a posto si leggono ma non si fanno notare.
 */
function Diagnosi({ events }: { events: JobEvent[] }) {
  const evento = [...events].reverse().find((e) => e.stage === 'diagnosi' && Array.isArray(e.details?.controlli))
  if (!evento) return null
  const controlli = (evento.details!.controlli as ControlloDiagnosi[]) ?? []
  const peso = { problema: 0, avviso: 1, spento: 2, ok: 3 }
  const ordinati = [...controlli].sort((a, b) => peso[a.esito] - peso[b.esito])
  const problemi = controlli.filter((c) => c.esito === 'problema').length
  const avvisi = controlli.filter((c) => c.esito === 'avviso').length
  const fornitore = String(evento.details?.fornitore ?? '')
  return (
    <section className={`diagnosi${problemi ? ' diagnosi--problemi' : avvisi ? ' diagnosi--avvisi' : ' diagnosi--ok'}`}>
      <header className="diagnosi__testa">
        <h3>{t('Diagnosi {fornitore}', { fornitore })}</h3>
        <span className="diagnosi__conteggi">
          {problemi > 0 && <span className="diagnosi__pill diagnosi__pill--problema">{t('{n} da sistemare', { n: problemi })}</span>}
          {avvisi > 0 && <span className="diagnosi__pill diagnosi__pill--avviso">{t('{n} avvisi', { n: avvisi })}</span>}
          <span className="diagnosi__pill diagnosi__pill--ok">
            {t('{n} a posto', { n: controlli.filter((c) => c.esito === 'ok').length })}
          </span>
        </span>
      </header>
      <table className="diagnosi__tabella">
        <tbody>
          {ordinati.map((c) => (
            <tr key={c.area + c.controllo} className={`diagnosi__riga diagnosi__riga--${c.esito}`}>
              <td className="diagnosi__icona" aria-label={c.esito}>
                {ICONA_ESITO[c.esito]}
              </td>
              <td>
                <div className="diagnosi__controllo">
                  <span className="diagnosi__area">{t(c.area)}</span> {t(c.controllo)}
                </div>
                {c.dettaglio && <div className="diagnosi__dettaglio">{t(c.dettaglio)}</div>}
                {c.rimedio && (
                  <div className="diagnosi__rimedio">
                    <strong>{t('Cosa fare:')}</strong> {t(c.rimedio)}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

